/* eslint-disable jsx-a11y/anchor-is-valid */
import axios from 'axios';
import Form from 'react-bootstrap/Form';
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { getRequestOptions } from "../components/GetToken";
import VehicleExpenseList from "../components/VehicleExpenseList";
import { KTSVG } from '../../_metronic/helpers';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { GoogleMap, LoadScript, Marker, Polyline } from '@react-google-maps/api';
import moment from 'moment';
import Swal from 'sweetalert2';
import ReactLoading from 'react-loading';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import userPermissionContext from '../../context/userPermissionContext';
import InputGroup from 'react-bootstrap/InputGroup';

const localizer = momentLocalizer(moment);
const API_URL = process.env.REACT_APP_API_URL || '';
const MAP_API_KEY = process.env.REACT_APP_MAP_API_KEY || '';

// Strip Google's leading Plus Code (e.g. "RVXV+VX9, ") from formatted addresses.
const PLUS_CODE_PREFIX = /^\s*[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}\s*,\s*/i;
const PLUS_CODE_ONLY = /^\s*[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}\s*$/i;
const cleanAddress = (text) => {
	if (!text) return '';
	let cleaned = text.replace(PLUS_CODE_PREFIX, '').trim();
	if (PLUS_CODE_ONLY.test(cleaned)) cleaned = '';
	return cleaned;
};

const parseStopMin = (text) => {
	if (!text || typeof text !== 'string') return 0;
	const h = Number((text.match(/(\d+)\s*h/) || [])[1] || 0);
	const m = Number((text.match(/(\d+)\s*min/) || [])[1] || 0);
	return h * 60 + m;
};

const parseTrackTime = (input) => {
	if (input === null || input === undefined || input === '') return NaN;
	if (typeof input === 'number' && Number.isFinite(input)) {
		return input < 1e12 ? input * 1000 : input;
	}
	const raw = String(input).trim();
	if (!raw) return NaN;
	const direct = new Date(raw).getTime();
	if (Number.isFinite(direct)) return direct;
	const isoLike = raw.replace(' ', 'T');
	const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoLike) ? isoLike : `${isoLike}Z`;
	const fallback = new Date(withZone).getTime();
	return Number.isFinite(fallback) ? fallback : NaN;
};

const formatRelativeTime = (timeStr) => {
	if (!timeStr) return '—';
	const t = parseTrackTime(timeStr);
	if (!Number.isFinite(t)) return String(timeStr);
	const diff = Date.now() - t;
	if (diff < 0 && diff > -60000) return 'just now';
	if (diff < 0) return String(timeStr);
	const sec = Math.floor(diff / 1000);
	if (sec < 5) return 'just now';
	if (sec < 60) return `${sec}s ago`;
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min} min ago`;
	const hr = Math.floor(min / 60);
	if (hr < 24) return `${hr}h ago`;
	return `${Math.floor(hr / 24)}d ago`;
};

const haversineKm = (lat1, lng1, lat2, lng2) => {
	const toRad = (deg) => (deg * Math.PI) / 180;
	const R = 6371;
	const dLat = toRad(lat2 - lat1);
	const dLng = toRad(lng2 - lng1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getBearingDeg = (lat1, lng1, lat2, lng2) => {
	const toRad = (deg) => (deg * Math.PI) / 180;
	const toDeg = (rad) => (rad * 180) / Math.PI;
	const dLng = toRad(lng2 - lng1);
	const y = Math.sin(dLng) * Math.cos(toRad(lat2));
	const x =
		Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
		Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
	return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// Shortest-arc angle interpolation: 350° → 10° rotates +20°, not -340°.
const lerpAngle = (a, b, t) => {
	const diff = ((((b - a) % 360) + 540) % 360) - 180;
	return (a + diff * t + 360) % 360;
};

const MAX_ROUTE_SAMPLE_POINTS = 25;

const sampleRoutePoints = (path, maxPoints = MAX_ROUTE_SAMPLE_POINTS) => {
	if (!Array.isArray(path) || path.length <= 2) return path;
	const step = Math.max(1, Math.ceil(path.length / maxPoints));
	const sampled = path.filter((_, index) => index % step === 0);
	const first = path[0];
	const last = path[path.length - 1];
	if (sampled[0] !== first) sampled.unshift(first);
	if (sampled[sampled.length - 1] !== last) sampled.push(last);
	return sampled.slice(0, maxPoints - 1).concat(last);
};

// Top-down Uber-style car icon, pre-rotated to `bearing` and tinted by `color`.
// Bearing is bucketed to 2° steps and the URL cached forever (~360 unique URLs)
// so animation doesn't rebuild/encode the SVG every frame.
const ICON_URL_CACHE = new Map();
const BEARING_BUCKET_DEG = 2;
const SELECTION_HIGHLIGHT_COLOR = '#2563eb';
const buildCarIconUrl = (bearing, color, isSelected = false) => {
	const bucket = ((Math.round(bearing / BEARING_BUCKET_DEG) * BEARING_BUCKET_DEG) % 360 + 360) % 360;
	const cacheKey = `${bucket}|${color}|${isSelected ? 'sel' : 'nrm'}`;
	const cached = ICON_URL_CACHE.get(cacheKey);
	if (cached) return cached;
	const size = isSelected ? 64 : 44;
	const half = size / 2;
	const halo = isSelected
		? `<circle cx="0" cy="0" r="22" fill="${SELECTION_HIGHLIGHT_COLOR}" opacity="0.18"/>` +
		  `<circle cx="0" cy="0" r="16" fill="none" stroke="${SELECTION_HIGHLIGHT_COLOR}" stroke-width="2.6" opacity="0.95"/>`
		: '';
	const svg =
		`<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="-${half} -${half} ${size} ${size}">` +
		halo +
		`<g transform="rotate(${bucket} 0 0)">` +
		`<ellipse cx="0" cy="2" rx="9" ry="14" fill="rgba(0,0,0,0.18)"/>` +
		`<rect x="-7.5" y="-13.5" width="15" height="27" rx="3.5" ry="3.5" fill="${color}" stroke="#ffffff" stroke-width="1.2"/>` +
		`<path d="M -5.5 -10 L 5.5 -10 L 4.5 -4 L -4.5 -4 Z" fill="#bfe0ff" opacity="0.95"/>` +
		`<path d="M -4.5 5 L 4.5 5 L 5.5 10 L -5.5 10 Z" fill="#bfe0ff" opacity="0.6"/>` +
		`<rect x="-6.2" y="-13" width="2.4" height="1.8" rx="0.6" fill="#fff8c2"/>` +
		`<rect x="3.8" y="-13" width="2.4" height="1.8" rx="0.6" fill="#fff8c2"/>` +
		`<rect x="-6.2" y="11.2" width="2.4" height="1.6" rx="0.5" fill="#ff5b5b"/>` +
		`<rect x="3.8" y="11.2" width="2.4" height="1.6" rx="0.5" fill="#ff5b5b"/>` +
		`</g></svg>`;
	const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
	ICON_URL_CACHE.set(cacheKey, url);
	return url;
};

const MARKER_OPTIONS = { optimized: false };
const MOVING_COLOR = '#111111';
const IDLE_COLOR = '#6c757d';

const InfoRow = ({ label, value, valueClassName = '' }) => (
	<div className="d-flex align-items-center py-2 px-3 border-bottom" style={{ minHeight: 38 }}>
		<div className="text-muted small" style={{ flex: '0 0 45%' }}>{label}</div>
		<div className={`fw-semibold flex-grow-1 ${valueClassName}`} style={{ wordBreak: 'break-word' }}>
			{value === null || value === undefined || value === '' ? <span className="text-muted">—</span> : value}
		</div>
	</div>
);

function DetailsVehicle() {
	const { id } = useParams();
	const { jwt } = useContext(userPermissionContext);
	const [details, setDetails] = useState({});
	const [comments, setComments] = useState("");
	const [tab, setTab] = useState("vehicleInfo");
	const [imageAttachment, setImageAttachment] = useState(null);
	const [file, setFile] = useState([]);
	const [image, setImage] = useState([]);
	const [imageTitle, setImageTitle] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [transferHistory, setTransferHistory] = useState([]);
	const [reload, setReload] = useState(false);
	const [liveItem, setLiveItem] = useState(null);
	const [locationName, setLocationName] = useState('');
	const [trackPath, setTrackPath] = useState([]);
	const [mapsReady, setMapsReady] = useState(false);
	const [snappedTarget, setSnappedTarget] = useState(null);
	const [animatedPos, setAnimatedPos] = useState(null);
	const [animatedBearing, setAnimatedBearing] = useState(0);
	const [snappedTrack, setSnappedTrack] = useState([]);
	const [isTrackSnapped, setIsTrackSnapped] = useState(false);
	const [isTrackVehicleModalOpen, setIsTrackVehicleModalOpen] = useState(false);
	const trackMapRef = useRef(null);
	const animationFrameRef = useRef(null);
	const distanceAnimationRef = useRef(null);
	const animatedPosRef = useRef(null);
	const animatedBearingRef = useRef(0);
	const displayedDistanceRef = useRef(0);
	const snapCacheRef = useRef(new Map());
	const [displayedTodaysKm, setDisplayedTodaysKm] = useState(0);

	useEffect(() => {
		animatedPosRef.current = animatedPos;
	}, [animatedPos]);
	useEffect(() => {
		animatedBearingRef.current = animatedBearing;
	}, [animatedBearing]);
	const tabChange = tab => {
		setTab(tab);
	}

	useEffect(() => {
		const vtsNo = String(details?.vtsNo || '').trim();
		const vehicleNumber = String(details?.vehicleNumber || '').trim();
		const deviceName = vehicleNumber;
		if (!deviceName) return;
		const trackerToken = localStorage.getItem('TrackerToken');
		if (!trackerToken || !jwt?.token) return;

		let cancelled = false;
		const fetchLive = () => {
			fetch(API_URL + 'liveTracData', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt.token },
				// Backend now accepts a `device_name` filter and forwards it to the upstream
				// carcopolo API, returning only this vehicle's live data instead of the full fleet.
				body: JSON.stringify({
					tracker_token: trackerToken,
					device_name: deviceName,
				}),
			})
				.then((r) => r.json())
				.then((resp) => {
					if (cancelled) return;
					if (!resp?.success || !Array.isArray(resp?.data) || resp.data.length === 0) return;
					const norm = (s) => String(s || '').trim().toLowerCase();
					// Prefer the first item (single-device response), but still match defensively
					// in case the upstream returns multiple rows.
					const match =
						resp.data.length === 1
							? resp.data[0]
							: resp.data.find((item) => {
								const dn = norm(item?.device_name);
								const nm = norm(item?.name);
								return (
									(vtsNo && (dn === norm(vtsNo) || nm === norm(vtsNo))) ||
									(vehicleNumber && (dn === norm(vehicleNumber) || nm === norm(vehicleNumber)))
								);
							}) || resp.data[0];
					if (!match) return;
					setLiveItem(match);
					const lat = Number(match?.latitude);
					const lng = Number(match?.longitude);
					if (Number.isFinite(lat) && Number.isFinite(lng)) {
						setTrackPath((prev) => {
							const last = prev[prev.length - 1];
							if (last && Math.abs(last.lat - lat) < 1e-6 && Math.abs(last.lng - lng) < 1e-6) {
								return prev;
							}
							return [...prev, { lat, lng }].slice(-200);
						});
					}
				})
				.catch(() => {});
		};

		fetchLive();
		const interval = setInterval(fetchLive, 10000);
		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [details?.vtsNo, details?.vehicleNumber, jwt?.token]);

	useEffect(() => {
		if (!liveItem || !MAP_API_KEY) return;
		const lat = Number(liveItem?.latitude);
		const lng = Number(liveItem?.longitude);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
		const controller = new AbortController();
		fetch(
			`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAP_API_KEY}`,
			{ signal: controller.signal }
		)
			.then((r) => r.json())
			.then((data) => {
				const raw = data?.results?.[0]?.formatted_address;
				const addr = cleanAddress(raw || '');
				if (addr) setLocationName(addr);
			})
			.catch(() => {});
		return () => controller.abort();
	}, [liveItem]);

	const liveStatus = useMemo(() => {
		if (!liveItem) return { label: 'Offline', dot: '#9ca3af', tone: 'secondary' };
		const speed = Number(liveItem?.speed) || 0;
		if (speed > 3) return { label: 'Moving', dot: '#16a34a', tone: 'success' };
		const stopMin = parseStopMin(String(liveItem?.stop_duration || ''));
		if (stopMin > 30) return { label: 'Stopped', dot: '#dc2626', tone: 'danger' };
		return { label: 'Idle', dot: '#f59e0b', tone: 'warning' };
	}, [liveItem]);

	const todaysKm = useMemo(() => {
		if (trackPath.length < 2) return 0;
		let total = 0;
		for (let i = 1; i < trackPath.length; i += 1) {
			total += haversineKm(trackPath[i - 1].lat, trackPath[i - 1].lng, trackPath[i].lat, trackPath[i].lng);
		}
		return total;
	}, [trackPath]);

	useEffect(() => {
		displayedDistanceRef.current = displayedTodaysKm;
	}, [displayedTodaysKm]);

	// Smoothly tween "Today's Distance" so the number grows naturally as route
	// points arrive, matching dashboard-style motion polish.
	useEffect(() => {
		const start = displayedDistanceRef.current;
		const end = todaysKm;
		if (!Number.isFinite(start) || !Number.isFinite(end) || Math.abs(end - start) < 0.0005) {
			displayedDistanceRef.current = end;
			setDisplayedTodaysKm(end);
			return;
		}

		if (distanceAnimationRef.current) cancelAnimationFrame(distanceAnimationRef.current);
		const startedAt = performance.now();
		const durationMs = 700;
		const tick = (now) => {
			const p = Math.min(1, (now - startedAt) / durationMs);
			const eased = 1 - Math.pow(1 - p, 3);
			const next = start + (end - start) * eased;
			displayedDistanceRef.current = next;
			setDisplayedTodaysKm(next);
			if (p < 1) {
				distanceAnimationRef.current = requestAnimationFrame(tick);
			} else {
				distanceAnimationRef.current = null;
			}
		};
		distanceAnimationRef.current = requestAnimationFrame(tick);
		return () => {
			if (distanceAnimationRef.current) {
				cancelAnimationFrame(distanceAnimationRef.current);
				distanceAnimationRef.current = null;
			}
		};
	}, [todaysKm]);

	const livePosition = useMemo(() => {
		const lat = Number(liveItem?.latitude);
		const lng = Number(liveItem?.longitude);
		return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
	}, [liveItem?.latitude, liveItem?.longitude]);

	// Snap raw GPS to the nearest road via Google Roads REST API so the marker
	// sits on the road instead of off in a field. Cached by 5dp coordinate
	// (~1.1m) — vehicles often report identical points repeatedly when slow.
	useEffect(() => {
		if (!livePosition || !MAP_API_KEY) return;
		const cacheKey = `${livePosition.lat.toFixed(5)},${livePosition.lng.toFixed(5)}`;
		const cached = snapCacheRef.current.get(cacheKey);
		if (cached) {
			setSnappedTarget(cached);
			return;
		}
		const controller = new AbortController();
		fetch(
			`https://roads.googleapis.com/v1/nearestRoads?points=${livePosition.lat},${livePosition.lng}&key=${MAP_API_KEY}`,
			{ signal: controller.signal }
		)
			.then((r) => r.json())
			.then((data) => {
				const sp = data?.snappedPoints?.[0];
				if (sp?.location && Number.isFinite(sp.location.latitude) && Number.isFinite(sp.location.longitude)) {
					const snapped = { lat: sp.location.latitude, lng: sp.location.longitude };
					snapCacheRef.current.set(cacheKey, snapped);
					setSnappedTarget(snapped);
				}
			})
			.catch(() => {});
		return () => controller.abort();
	}, [livePosition]);

	// Smoothly animate the marker from its current rendered position to the new
	// target (snapped if we have it, raw otherwise) over ~1.8s, also rotating
	// the icon along the shortest-arc bearing. Same approach as the dashboard.
	useEffect(() => {
		const target = snappedTarget || livePosition;
		if (!target) return;

		const from = animatedPosRef.current || target;
		const movedKm = haversineKm(from.lat, from.lng, target.lat, target.lng);
		const targetBearing = movedKm > 0.001
			? getBearingDeg(from.lat, from.lng, target.lat, target.lng)
			: animatedBearingRef.current;
		const startBearing = animatedBearingRef.current ?? targetBearing ?? 0;

		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
		}

		const durationMs = 1800;
		const startTime = performance.now();

		const tick = (now) => {
			const progress = Math.min(1, (now - startTime) / durationMs);
			const eased = progress < 1 ? 1 - Math.pow(1 - progress, 3) : 1;
			setAnimatedPos({
				lat: from.lat + (target.lat - from.lat) * eased,
				lng: from.lng + (target.lng - from.lng) * eased,
			});
			setAnimatedBearing(lerpAngle(startBearing, targetBearing, eased));
			if (progress < 1) {
				animationFrameRef.current = requestAnimationFrame(tick);
			} else {
				animationFrameRef.current = null;
			}
		};

		animationFrameRef.current = requestAnimationFrame(tick);
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
		};
	}, [livePosition, snappedTarget]);

	// Snap the breadcrumb polyline to actual roads via Google Directions API,
	// so the trail follows roads instead of cutting straight across buildings.
	// Falls back to the raw track on failure.
	useEffect(() => {
		if (!mapsReady || !window.google?.maps?.DirectionsService || trackPath.length < 2) {
			setSnappedTrack([]);
			setIsTrackSnapped(false);
			return;
		}
		const sampled = sampleRoutePoints(trackPath.map((p) => ({ lat: p.lat, lng: p.lng })));
		if (sampled.length < 2) {
			setSnappedTrack([]);
			setIsTrackSnapped(false);
			return;
		}
		let cancelled = false;
		const service = new window.google.maps.DirectionsService();
		const apply = (result, status) => {
			if (cancelled) return false;
			const route = result?.routes?.[0];
			if (status === 'OK' && route?.overview_path?.length > 1) {
				const path = route.overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
				setSnappedTrack(path);
				setIsTrackSnapped(true);
				return true;
			}
			return false;
		};
		service.route(
			{
				origin: sampled[0],
				destination: sampled[sampled.length - 1],
				waypoints: sampled.slice(1, -1).map((p) => ({ location: p, stopover: false })),
				optimizeWaypoints: false,
				travelMode: window.google.maps.TravelMode.DRIVING,
			},
			(result, status) => {
				const ok = apply(result, status);
				if (ok || cancelled) return;
				service.route(
					{
						origin: sampled[0],
						destination: sampled[sampled.length - 1],
						travelMode: window.google.maps.TravelMode.DRIVING,
					},
					(result2, status2) => {
						const ok2 = apply(result2, status2);
						if (!ok2 && !cancelled) {
							setSnappedTrack([]);
							setIsTrackSnapped(false);
						}
					}
				);
			}
		);
		return () => {
			cancelled = true;
		};
	}, [mapsReady, trackPath]);

	useEffect(() => {
		if (!trackMapRef.current) return;
		const target = animatedPos || snappedTarget || livePosition;
		if (target) trackMapRef.current.panTo(target);
	}, [animatedPos, snappedTarget, livePosition]);

	// Stable google.maps Size/Point — built once after the JS API loads,
	// otherwise rebuilding them every render busts the Marker cache.
	const iconSize = useMemo(
		() => (mapsReady && window.google?.maps?.Size ? new window.google.maps.Size(44, 44) : undefined),
		[mapsReady]
	);
	const iconAnchor = useMemo(
		() => (mapsReady && window.google?.maps?.Point ? new window.google.maps.Point(22, 22) : undefined),
		[mapsReady]
	);
	const iconSizeSelected = useMemo(
		() => (mapsReady && window.google?.maps?.Size ? new window.google.maps.Size(64, 64) : undefined),
		[mapsReady]
	);
	const iconAnchorSelected = useMemo(
		() => (mapsReady && window.google?.maps?.Point ? new window.google.maps.Point(32, 32) : undefined),
		[mapsReady]
	);

	useEffect(() => {
		if (!isTrackVehicleModalOpen) return;
		const onKey = (e) => {
			if (e.key === 'Escape') setIsTrackVehicleModalOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [isTrackVehicleModalOpen]);

	useEffect(() => {
		const getVehicleDetails = () => {
			fetch(API_URL + "vehicleDetails/" + id, getRequestOptions())
				.then((resp) => {
					return resp.json()
				})
				.then((resp) => {
					setDetails(resp.data);
					if (resp.data.transferHistory.length > 0) {
						var tempArray = resp.data.transferHistory.map(item => {
							item.start = new Date(item.start.split("-")[0], (Number(item.start.split("-")[1]) - 1), item.start.split("-")[2]);
							item.end = new Date(item.end.split("-")[0], (Number(item.end.split("-")[1]) - 1), item.end.split("-")[2]);
							return item;
						})
					}
					setTransferHistory(tempArray);
				})
				.catch((error) => {
					console.log(error, "catch the hoop")
				});
		}
		getVehicleDetails();
	}, [id]);

	useEffect(() => {
		const getVehicleFiles = () => {
			fetch(API_URL + "vehicleFiles/" + id, getRequestOptions())
				.then((resp) => {
					return resp.json()
				})
				.then((resp) => {
					setFile(resp.data.files);
					setImage(resp.data.images);
					setReload(false);
				})
				.catch((error) => {
					console.log(error, "catch the hoop")
				});
		}

		getVehicleFiles();
	}, [id, reload]);

	const onImageChange = event => {
		setImageAttachment({ ...imageAttachment, image: event.target.files[0], lastModified: event.target.files[0].lastModified, name: event.target.files[0].name, size: event.target.files[0].size, type: event.target.files[0].type });
	}

	const handleInputOnChange = (e) => {
		setImageTitle(e.target.value);
	}

	const resetAttachedUploadImage = () => {
		setImageAttachment([]);
	}

	const handleAttachedImageSubmit = evt => {
		evt.preventDefault();
		Swal.fire({
			title: 'Are you sure?',
			text: "You want to upload this attached image!",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'No'
		}).then((result) => {
			if (result.value) {
				if (imageAttachment.image) {
					const formData = new FormData();
					formData.append('attachedImage', imageAttachment.image);
					formData.append("idVmsVehicleInfo", id);
					formData.append("imageTitle", imageTitle);
					setIsLoading(true);
					axios.post(API_URL + "vehicleImageUpload", formData, {
						headers: { 'Content-Type': 'multipart/form-data', 'Authorization': 'Bearer ' + jwt.token },
					})
						.then(response => {
							if (response.data.success === true) {
								Swal.fire({
									icon: 'success',
									title: 'Success',
									text: response.data.successMessage,
								})
								setImageAttachment([]);
								setIsLoading(false);
								setImageTitle("");
								setReload(true);
							}
							else {
								Swal.fire({
									icon: 'error',
									title: 'Error',
									html: response.data.errorMessage
								})
								setIsLoading(false);
							}
						})
						.catch((error) => {
							Swal.fire({
								icon: 'error',
								title: 'Error',
								html: error.response.data.errorMessage
							})
							setIsLoading(false);
						})
				}
				else {
					Swal.fire({
						icon: 'error',
						title: 'Error',
						html: 'Please attach a image before submit'
					})
					setIsLoading(false);
				}
			}
		})
	}

	const isMainChange = (idVmsVehicleImages) => {
		const formData = { idVmsVehicleImages: idVmsVehicleImages };
		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + jwt.token },
			body: JSON.stringify(formData)
		};
		fetch(API_URL + "setVehicleMainImage", requestOptions)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				if (resp.success === true) {
					Swal.fire({
						position: "top-right",
						icon: "success",
						title: "Main Image Changed",
						showConfirmButton: false
					});
					setReload(true);
				}
				else {
					var errorsMessage = [];

					if (resp.errorMessage !== undefined && typeof resp.errorMessage === 'object') {
						var errorsObj = resp.errorMessage;
						Object.keys(errorsObj).forEach(function (value) {
							errorsObj[value].forEach(function (v) {
								errorsMessage.push(v)
							});

						});

					} else if (resp.errorMessage !== undefined && (typeof resp.errorMessage === 'string' || resp.errorMessage instanceof String)) {
						errorsMessage.push(resp.errorMessage);
					} else {

						errorsMessage.push("Something went wrong");
					}
					Swal.fire({
						icon: 'error',
						title: resp.heading,
						text: errorsMessage,
					})
				}
			})
			.catch((error) => {
				console.log(error, "catch the hoop")
			});
	}

	const removeFile = (idVmsVehicleImages) => {
		Swal.fire({
			title: 'Are you sure?',
			text: "You want to delete this image!",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'No'
		}).then((result) => {
			if (result.value) {
				const requestOptions = {
					method: 'DELETE',
					headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + jwt.token },
				};
				fetch(API_URL + `removeVehicleImage/${idVmsVehicleImages}`, requestOptions)
					.then((resp) => {
						return resp.json()
					})
					.then((resp) => {
						if (resp.success === true) {
							Swal.fire({
								position: "top-right",
								icon: "success",
								title: "Image Deleted",
								showConfirmButton: false
							});
							setReload(true);
						}
						else {
							var errorsMessage = [];

							if (resp.errorMessage !== undefined && typeof resp.errorMessage === 'object') {
								var errorsObj = resp.errorMessage;
								Object.keys(errorsObj).forEach(function (value) {
									errorsObj[value].forEach(function (v) {
										errorsMessage.push(v)
									});

								});

							} else if (resp.errorMessage !== undefined && (typeof resp.errorMessage === 'string' || resp.errorMessage instanceof String)) {
								errorsMessage.push(resp.errorMessage);
							} else {

								errorsMessage.push("Something went wrong");
							}
							Swal.fire({
								icon: 'error',
								title: resp.heading,
								text: errorsMessage,

							})
						}
					})
					.catch((error) => {
						console.log(error, "catch the hoop")
					});
			}
		})
	}

	const addComments = () => {
		const formData = { comments: comments, idVmsVehicleInfo: id };
		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + jwt.token },
			body: JSON.stringify(formData)
		};
		fetch(API_URL + "addComments", requestOptions)
			.then((resp) => {
				return resp.json()
			})
			.then((resp) => {
				if (resp.success === true) {
					setComments("");
					setReload(true);
				}
				else {
					var errorsMessage = [];

					if (resp.errorMessage !== undefined && typeof resp.errorMessage === 'object') {
						var errorsObj = resp.errorMessage;
						Object.keys(errorsObj).forEach(function (value) {
							errorsObj[value].forEach(function (v) {
								errorsMessage.push(v)
							});

						});

					} else if (resp.errorMessage !== undefined && (typeof resp.errorMessage === 'string' || resp.errorMessage instanceof String)) {
						errorsMessage.push(resp.errorMessage);
					} else {

						errorsMessage.push("Something went wrong");
					}
					Swal.fire({
						icon: 'error',
						title: resp.heading,
						text: errorsMessage,
					})
				}
			})
			.catch((error) => {
				console.log(error, "catch the hoop")
			});
	}



	function Event({ event }) {
		return (
			<span>
				<strong>{event.title}</strong>
				<br></br>
				{event.userName !== "" ? event.userName + "( " + event.designation + " )" : ""}
			</span>
		)
	}

	const filehost = process.env.REACT_APP_AWS_URL + 'vms/uploads/';
	const filePath = id + '/renewal';
	const imagePath = id + '/images';
	return (
		<>
			<div className="card card-custom">
				<div className="card-header">
					<div className="card-title">
						<KTSVG
							path='/media/svg/icons/Code/Info-circle.svg'
							className='svg-icon-1 svg-icon-primary'
						/>
						<span className="text fw-bolder" style={{ marginLeft: "10px" }}>
							VEHICLE INFORMATION
						</span>
					</div>
				</div>
				<div className="card-body">
					<ul className="nav nav-tabs nav-tabs-line">
						<li className="nav-item" onClick={() => tabChange("vehicleInfo")}>
							<a className={`nav-link ${tab === "vehicleInfo" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleInfo").toString()}>Basic Info</a>
						</li>
						<li className="nav-item" onClick={() => tabChange("vehicleTrack")}>
							<a className={`nav-link d-inline-flex align-items-center ${tab === "vehicleTrack" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleTrack").toString()}>
								<span
									style={{
										display: 'inline-block',
										width: 8,
										height: 8,
										borderRadius: '50%',
										backgroundColor: liveStatus.dot,
										marginRight: 6,
									}}
								></span>
								Track
							</a>
						</li>
						<li className="nav-item" onClick={() => tabChange("vehicleInsurance")}>
							<a className={`nav-link ${tab === "vehicleInsurance" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleInsurance").toString()}>Insurance</a>
						</li>
						<li className="nav-item" onClick={() => tabChange("vehicleComments")}>
							<a className={`nav-link ${tab === "vehicleComments" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleComments").toString()}>Comments</a>
						</li>
						<li className="nav-item" onClick={() => tabChange("vehicleFiles")}>
							<a className={`nav-link ${tab === "vehicleFiles" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleFiles").toString()}>Files</a>
						</li>
						<li className="nav-item" onClick={() => tabChange("vehicleExpenseList")}>
							<a className={`nav-link ${tab === "vehicleExpenseList" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleExpenseList").toString()}>Expense List</a>
						</li>
						<li className="nav-item" onClick={() => tabChange("vehicleHistory")}>
							<a className={`nav-link ${tab === "vehicleHistory" && "active"}`} data-toggle="tab" role="tab" aria-selected={(tab === "vehicleHistory").toString()}>History</a>
						</li>
					</ul>

					{tab === "vehicleInfo" &&
						<>
							<div
								className="rounded mt-4 mb-4 d-flex flex-wrap align-items-stretch"
								style={{ backgroundColor: '#e8f5ec', border: '1px solid #d1e9d9' }}
							>
								<div className="px-4 py-3 flex-grow-1" style={{ borderRight: '1px solid #d1e9d9', minWidth: 220 }}>
									<div className="text-uppercase text-muted small mb-1 d-flex align-items-center">
										<i className="bi bi-geo-alt-fill me-1" style={{ color: '#0d6efd' }}></i>
										Current Location
									</div>
									<div className="fw-bolder text-dark" style={{ fontSize: '1rem' }}>
										{locationName ||
											(livePosition ? `${livePosition.lat.toFixed(5)}, ${livePosition.lng.toFixed(5)}` : '—')}
									</div>
									<div className="small text-muted">
										Updated {formatRelativeTime(liveItem?.time || liveItem?.device_time || liveItem?.datetime)}
									</div>
								</div>
								<div className="px-4 py-3" style={{ borderRight: '1px solid #d1e9d9', minWidth: 140 }}>
									<div className="text-uppercase text-muted small mb-1">Status</div>
									<div className="fw-bolder text-dark d-flex align-items-center" style={{ fontSize: '1rem' }}>
										<span
											style={{
												display: 'inline-block',
												width: 8,
												height: 8,
												borderRadius: '50%',
												backgroundColor: liveStatus.dot,
												marginRight: 8,
											}}
										></span>
										{liveStatus.label}
									</div>
								</div>
								<div className="px-4 py-3" style={{ borderRight: '1px solid #d1e9d9', minWidth: 130 }}>
									<div className="text-uppercase text-muted small mb-1">Speed</div>
									<div className="text-dark" style={{ fontSize: '1rem' }}>
										<span className="fw-bolder fs-3">{Math.round(Number(liveItem?.speed || 0))}</span>
										<span className="text-muted small ms-1">km/h</span>
									</div>
								</div>
								<div className="px-4 py-3 flex-grow-1" style={{ minWidth: 160 }}>
									<div className="text-uppercase text-muted small mb-1">Today</div>
									<div className="text-dark" style={{ fontSize: '1rem' }}>
										<span className="fw-bolder fs-3">{Math.round(todaysKm)}</span>
										<span className="text-muted small ms-1">km</span>
									</div>
									<div className="small text-muted">
										Ignition {Number(liveItem?.speed) > 0 ? 'ON' : 'OFF'} · 0 trips
									</div>
								</div>
								<div className="px-3 py-3 d-flex align-items-center">
									<button
										type="button"
										className="btn btn-light btn-sm fw-semibold"
										onClick={() => tabChange('vehicleTrack')}
									>
										<i className="bi bi-arrows-fullscreen me-1"></i>
										Open Track
									</button>
								</div>
							</div>

							<div className="row g-3">
								<div className="col-md-4">
									<div className="card border">
										<InfoRow label="Owner" value={details.vehicleOwner} />
										<InfoRow label="Payable / Non Payable Item" value={details.usageType} />
										<InfoRow
											label="Vehicle Number"
											value={details.vehicleNumber}
											valueClassName="text-primary"
										/>
										{details.vehicleOwner === 'In House' && (
											<>
												<InfoRow label="Fixed Asset Code" value={details.faCode} />
												<InfoRow label="Fixed Asset Project" value={details.faProjectName} />
											</>
										)}
										{details.vehicleOwner === 'Third Party' && (
											<InfoRow label="Vendor" value={details.vendorName} />
										)}
										<InfoRow label="Category" value={details.categoryName} />
										<InfoRow label="Vehicle Type" value={details.vehicleType} />
										<InfoRow label="Color" value={details.colorName} />
										<InfoRow label="Brand" value={details.productName} />
										<InfoRow label="Model" value={details.modelName} />
										<InfoRow
											label="Rental Type"
											value={
												details.rentalType
													? details.rentalType
															.split('-')
															.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
															.join(' ')
													: ''
											}
										/>
										<InfoRow
											label="Car Use Type"
											value={
												details.carUseType
													? details.carUseType.charAt(0).toUpperCase() + details.carUseType.slice(1)
													: ''
											}
										/>
										<InfoRow label="Name Of Registration" value={details.nameOfRegistration} />
										<InfoRow label="Purchase Date" value={details.purchaseDate} />
										<InfoRow label="Chassis No" value={details.chassisNo} />
										<InfoRow
											label="VTS/ GPS"
											value={
												details.vtsOrGps
													? details.vtsOrGps.charAt(0).toUpperCase() + details.vtsOrGps.slice(1)
													: ''
											}
											valueClassName={details.vtsOrGps === 'yes' ? 'text-primary' : ''}
										/>
										<InfoRow label="VTS No" value={details.vtsNo} />
										<InfoRow
											label="VTS/GPS Functioning"
											value={
												details.vtsOrGpsFunctioning
													? details.vtsOrGpsFunctioning.charAt(0).toUpperCase() +
													  details.vtsOrGpsFunctioning.slice(1)
													: ''
											}
											valueClassName={details.vtsOrGpsFunctioning === 'yes' ? 'text-primary' : ''}
										/>
									</div>
								</div>
								<div className="col-md-4">
									<div className="card border">
										<InfoRow label="Year Of Manufacture" value={details.yearOfManufacture} />
										<InfoRow label="E Tracking Number" value={details.eTrackingNumber} />
										<InfoRow label="Overhang" value={details.overHang} />
										<InfoRow label="Transaction Number" value={details.transactionNumber} />
										<InfoRow label="Current Meter Reading" value={details.currentMeterReading} />
										<InfoRow label="Fixed Amount" value={details.fixedAmount} />
										<InfoRow label="Milage" value={details.milage} />
										<InfoRow label="Monthly Expense" value={details.monthlyExpense} />
										<InfoRow label="Last Review Date" value={details.lastReviewDate} />
										<InfoRow label="Last Policy Review On" value={details.lastPolicyReviewOn} />
										<InfoRow label="Registration Date" value={details.registrationDate} />
										<InfoRow label="Tax Amount" value={details.taxAmount} />
										<InfoRow label="Tax Token Expiration Date" value={details.taxTokenExpirationDate} />
										<InfoRow label="Fitness Expiration Date" value={details.fitnessExpirationDate} />
										<InfoRow label="Route Permit Expiration Date" value={details.routePermitExpirationDate} />
									</div>
								</div>
								<div className="col-md-4">
									<div className="card border">
										{details.vehicle_details && (
											<>
												<InfoRow label="No Of Tyres" value={details.vehicle_details.noOfTyres} />
												<InfoRow label="Tyre Size" value={details.vehicle_details.tyreSize} />
												<InfoRow label="Seat Capacity" value={details.vehicle_details.seatCapacity} />
												<InfoRow label="Spare Tyres" value={details.vehicle_details.spareTyres} />
												<InfoRow
													label="Run By"
													value={
														details.vehicle_details.runBy
															? details.vehicle_details.runBy.charAt(0).toUpperCase() +
															  details.vehicle_details.runBy.slice(1)
															: ''
													}
												/>
												<InfoRow label="Fuel Capacity" value={details.vehicle_details.fuelCapacity} />
												<InfoRow label="Cylinder Capacity" value={details.vehicle_details.cylinderCapacity} />
												<InfoRow label="Engine Number" value={details.vehicle_details.engineNumber} />
												<InfoRow label="Laden Weight (kg)" value={details.vehicle_details.ladenWeight} />
												<InfoRow
													label="Engine (CC) Displacement"
													value={details.vehicle_details.engineCcDisplacement}
												/>
											</>
										)}
										<InfoRow
											label="Current Project"
											value={details.currentProjectName}
											valueClassName="text-primary"
										/>
										{details.currentUserName !== null && (
											<>
												<InfoRow
													label="Current User"
													value={`${details.currentUserName} (${details.currentUserDesignation})`}
												/>
												<InfoRow label="Current User Phone" value={details.currentUserPhone} />
											</>
										)}
										{details.consultantOrAdvisorName !== null && (
											<InfoRow label="Consultant Or Advisor" value={details.consultantOrAdvisorName} />
										)}
										{details.consultantOrAdvisorDesignation !== null && (
											<InfoRow
												label="Consultant Or Advisor Designation"
												value={details.consultantOrAdvisorDesignation}
											/>
										)}
										<InfoRow label="Self Driven" value={details.selfDriven === 'yes' ? 'Yes' : 'No'} />
										{details.currentDriverName !== null && (
											<InfoRow
												label="Current Driver"
												value={`${details.currentDriverName} (${details.driverMobileNumber})`}
											/>
										)}
										<InfoRow label="Created By" value={details.createdUser} />
										<InfoRow label="Created Date" value={details.createDate} />
										<InfoRow label="Last Updated By" value={details.updatedBy} />
										<InfoRow label="Last Updated Date" value={details.updatedDate} />
										<InfoRow
											label="Functional Status"
											value={
												details.functionalStatus
													? details.functionalStatus
															.split('-')
															.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
															.join(' ')
													: ''
											}
										/>
										{details.repairType && (
											<InfoRow
												label="Repair Type"
												value={details.repairType.charAt(0).toUpperCase() + details.repairType.slice(1) + ' Repair'}
											/>
										)}
										<InfoRow
											label="Status"
											value={
												details.vehicleStatus === 'Active' ? (
													<span className="badge badge-light-success fw-bolder">
														<span
															style={{
																display: 'inline-block',
																width: 6,
																height: 6,
																borderRadius: '50%',
																backgroundColor: '#16a34a',
																marginRight: 4,
															}}
														></span>
														Active
													</span>
												) : (
													<span className="badge badge-light-danger fw-bolder">Inactive</span>
												)
											}
										/>
										{details.inactiveStatus && (
											<InfoRow label="Inactive Status" value={details.inactiveStatus} />
										)}
										{details.remarks && <InfoRow label="Remarks" value={details.remarks} />}
										{details?.vehicle_details?.parkingLocation && (
											<InfoRow label="Parking Location" value={details.vehicle_details.parkingLocation} />
										)}
									</div>
								</div>
							</div>
						</>
					}

					{tab === "vehicleTrack" &&
						<div className="mt-4">
							<div className="row g-3">
								<div className="col-md-3">
									<div
										className="rounded p-3 mb-3"
										style={{ backgroundColor: '#e8f5ec', border: '1px solid #d1e9d9' }}
									>
										<div className="text-uppercase text-muted small mb-1">Current Location</div>
										<div className="fw-bolder text-dark mb-1">
											{locationName || (livePosition ? 'Resolving address…' : '—')}
										</div>
										{livePosition && (
											<div className="small text-muted">
												Lat {Math.abs(livePosition.lat).toFixed(2)}° {livePosition.lat >= 0 ? 'N' : 'S'},
												{' '}Lon {Math.abs(livePosition.lng).toFixed(2)}° {livePosition.lng >= 0 ? 'E' : 'W'}
											</div>
										)}
										<div className="small text-muted">
											Updated {formatRelativeTime(liveItem?.time || liveItem?.device_time || liveItem?.datetime)}
										</div>
									</div>

									<div className="row g-2 mb-3">
										<div className="col-6">
											<div className="border rounded p-2 h-100">
												<div className="text-uppercase text-muted small mb-1">
													<i className="bi bi-circle-fill me-1" style={{ fontSize: '0.5rem', color: liveStatus.dot }}></i>
													Status
												</div>
												<span className={`badge badge-light-${liveStatus.tone} fw-bolder`}>
													<span
														style={{
															display: 'inline-block',
															width: 6,
															height: 6,
															borderRadius: '50%',
															backgroundColor: liveStatus.dot,
															marginRight: 4,
														}}
													></span>
													{liveStatus.label}
												</span>
											</div>
										</div>
										<div className="col-6">
											<div className="border rounded p-2 h-100">
												<div className="text-uppercase text-muted small mb-1">
													<i className="bi bi-speedometer2 me-1"></i>
													Speed
												</div>
												<div className="fw-bolder">
													{Math.round(Number(liveItem?.speed || 0))}
													<span className="text-muted small fw-normal ms-1">km/h</span>
												</div>
											</div>
										</div>
										<div className="col-6">
											<div className="border rounded p-2 h-100">
												<div className="text-uppercase text-muted small mb-1">
													<i className="bi bi-signpost-split me-1"></i>
													Today's Distance
												</div>
												<div className="fw-bolder">
													{displayedTodaysKm.toFixed(1)}
													<span className="text-muted small fw-normal ms-1">km</span>
												</div>
												<div className="small text-muted" style={{ fontSize: '0.7rem' }}>
													{trackPath.length > 1
														? isTrackSnapped
															? 'Route snapped to road'
															: 'Snapping…'
														: 'Awaiting movement'}
												</div>
											</div>
										</div>
										<div className="col-6">
											<div className="border rounded p-2 h-100">
												<div className="text-uppercase text-muted small mb-1">
													<i className="bi bi-key me-1"></i>
													Ignition
												</div>
												<div className="fw-bolder">
													{Number(liveItem?.speed) > 0 ? 'ON' : 'OFF'}
												</div>
											</div>
										</div>
									</div>

									<button
										type="button"
										className="btn btn-light btn-sm w-100 mb-2"
										onClick={() => {
											const device = String(details?.vehicleNumber || '').trim();
											const baseHref = window.location.href.split('#')[0];
											const url =
												`${baseHref}#/vehicleTrackHistory` +
												(device ? `?device=${encodeURIComponent(device)}` : '');
											window.open(url, '_blank', 'noopener,noreferrer');
										}}
									>
										<i className="bi bi-clock-history me-1"></i>
										<span className="small">Trip history</span>
									</button>

								</div>

								<div className="col-md-9">
									<div className="card border">
										<div className="card-body p-0">
											{MAP_API_KEY ? (
												<LoadScript googleMapsApiKey={MAP_API_KEY} onLoad={() => setMapsReady(true)}>
													<GoogleMap
														zoom={15}
														mapContainerStyle={{ height: '560px', width: '100%' }}
														center={animatedPos || snappedTarget || livePosition || { lat: 23.7806, lng: 90.407 }}
														options={{ mapTypeControl: true }}
														onLoad={(map) => {
															trackMapRef.current = map;
														}}
													>
														{snappedTrack.length > 1 && (
															<Polyline
																path={snappedTrack}
																options={{
																	strokeColor: '#198754',
																	strokeOpacity: 0.95,
																	strokeWeight: 4,
																}}
															/>
														)}
														{snappedTrack.length < 2 && trackPath.length > 1 && (
															<Polyline
																path={trackPath}
																options={{
																	strokeColor: '#0d6efd',
																	strokeOpacity: 0.6,
																	strokeWeight: 3,
																}}
															/>
														)}
														{(animatedPos || snappedTarget || livePosition) && mapsReady && (
															<Marker
																position={animatedPos || snappedTarget || livePosition}
																onClick={() => setIsTrackVehicleModalOpen(true)}
																zIndex={9999}
																options={MARKER_OPTIONS}
																icon={{
																	url: buildCarIconUrl(
																		animatedBearing,
																		Number(liveItem?.speed || 0) > 3 ? MOVING_COLOR : IDLE_COLOR,
																		true
																	),
																	scaledSize: iconSizeSelected || iconSize,
																	anchor: iconAnchorSelected || iconAnchor,
																}}
															/>
														)}
													</GoogleMap>
												</LoadScript>
											) : (
												<div className="p-5 text-center text-muted">Google Maps API key not configured.</div>
											)}
										</div>
									</div>
								</div>
							</div>
							{isTrackVehicleModalOpen && (
								<div
									onClick={() => setIsTrackVehicleModalOpen(false)}
									style={{
										position: 'fixed',
										inset: 0,
										background: 'rgba(15,23,42,0.55)',
										backdropFilter: 'blur(2px)',
										WebkitBackdropFilter: 'blur(2px)',
										zIndex: 100000,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										padding: 16,
									}}
								>
									<div
										onClick={(e) => e.stopPropagation()}
										style={{
											width: 360,
											maxWidth: '100%',
											background: '#fff',
											borderRadius: 14,
											overflow: 'hidden',
											boxShadow: '0 20px 48px rgba(15,23,42,0.28), 0 4px 12px rgba(15,23,42,0.18)',
										}}
									>
										<div
											style={{
												background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
												color: '#fff',
												padding: '14px 14px 16px',
												position: 'relative',
											}}
										>
											<div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.15, paddingRight: 36, wordBreak: 'break-word' }}>
												{details.vehicleNumber || details.name || 'Vehicle'}
											</div>
											<div style={{ fontSize: 12, opacity: 0.95, marginTop: 4 }}>
												Updated {formatRelativeTime(liveItem?.time || liveItem?.device_time || liveItem?.datetime)}
											</div>
											<button
												type="button"
												onClick={() => setIsTrackVehicleModalOpen(false)}
												aria-label="Close"
												style={{
													position: 'absolute',
													top: 10,
													right: 10,
													height: 28,
													width: 28,
													borderRadius: 8,
													background: 'rgba(255,255,255,0.22)',
													color: '#fff',
													border: 'none',
													cursor: 'pointer',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontWeight: 700,
													fontSize: 16,
													lineHeight: 1,
												}}
											>
												×
											</button>
										</div>

										<div style={{ padding: 14 }}>
											<div className="row g-2">
												<div className="col-6">
													<div className="border rounded p-2 h-100">
														<div className="text-uppercase text-muted small mb-1">Speed</div>
														<div className="fw-bolder">
															{Math.round(Number(liveItem?.speed || 0))}
															<span className="text-muted small fw-normal ms-1">km/h</span>
														</div>
													</div>
												</div>
												<div className="col-6">
													<div className="border rounded p-2 h-100">
														<div className="text-uppercase text-muted small mb-1">Status</div>
														<div className="fw-bolder d-flex align-items-center">
															<span
																style={{
																	display: 'inline-block',
																	width: 8,
																	height: 8,
																	borderRadius: '50%',
																	backgroundColor: liveStatus.dot,
																	marginRight: 8,
																}}
															></span>
															{liveStatus.label}
														</div>
													</div>
												</div>
												<div className="col-6">
													<div className="border rounded p-2 h-100">
														<div className="text-uppercase text-muted small mb-1">Distance</div>
														<div className="fw-bolder">
															{displayedTodaysKm.toFixed(1)}
															<span className="text-muted small fw-normal ms-1">km</span>
														</div>
													</div>
												</div>
												<div className="col-6">
													<div className="border rounded p-2 h-100">
														<div className="text-uppercase text-muted small mb-1">Ignition</div>
														<div className="fw-bolder">{Number(liveItem?.speed) > 0 ? 'ON' : 'OFF'}</div>
													</div>
												</div>
											</div>

											<div className="border rounded p-2 mt-2">
												<div className="text-uppercase text-muted small mb-1">Location</div>
												<div className="fw-semibold" style={{ wordBreak: 'break-word' }}>
													{locationName || (livePosition ? `${livePosition.lat.toFixed(5)}, ${livePosition.lng.toFixed(5)}` : '—')}
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					}
					{tab === "vehicleInsurance" &&
						<div className="col-md-8 mt-5">
							<table className="table table-sm table-hover  table-striped table-bordered">
								<tbody>
									<tr>
										<th>Insurance Type</th>
										<td>{details.vehicle_insurance.insuranceType}</td>
									</tr>
									<tr>
										<th>Insurance Company</th>
										<td>{details.vehicle_insurance.insuranceCompany}</td>
									</tr>
									<tr>
										<th>Insurance Policy Number</th>
										<td>{details.vehicle_insurance.insurancePolicyNumber}</td>
									</tr>
									<tr>
										<th>Insurance Expiration Date</th>
										<td>{details.vehicle_insurance.insuranceExpirationDate}</td>
									</tr>
									<tr>
										<th>Yearly Premium</th>
										<td>{details.vehicle_insurance.yearlyPremium}</td>
									</tr>
									<tr>
										<th>Source</th>
										<td>{details.vehicle_insurance.source}</td>
									</tr>
								</tbody>
							</table>
						</div>
					}
					{tab === "vehicleComments" &&
						<>
							<div className="col-md-8 mt-5">
								<InputGroup size='sm'>
									<Form.Control
										placeholder="Comments"
										aria-label="Comments"
										aria-describedby="basic-addon2"
										value={comments}
										onChange={(e) => setComments(e.target.value)}
									/>
									<button className="btn btn-success btn-sm" type="button" onClick={addComments} disabled={comments === ""}>Add</button>
								</InputGroup>
							</div>
							<div className='card-body pt-5'>
								<div className='timeline-label'>
									{details.vehicle_comments && details.vehicle_comments.length > 0 && details.vehicle_comments.map((item, index) => (

										<div className='timeline-item' key={index}>
											<div className='timeline-label fw-bold text-gray-800' >
												{/* {item.commentorName} */}
											</div>
											<div className='timeline-badge'>
												<i className='fa fa-genderless text-warning fs-1'></i>
											</div>
											<div className='fw-mormal timeline-content text-gray-800 ps-3'>
												{item.comments}
											</div>
										</div>

									))}

								</div>
							</div>
						</>
					}
					{tab === "vehicleHistory" &&
						<div className='row'>
							<div className='col-md-6'>
								<div className="table-responsive mt-5" style={{ "overflowY": "auto" }}>
									<table className="table table-striped table-bordered">
										<thead>
											<tr>
												<th>Sl</th>
												<th>Transfer Date</th>
												<th>Transfer From</th>
												<th>Transfer To</th>
												<th>From User</th>
												<th>To User</th>
											</tr>
										</thead>
										<tbody>
											{details.vehicle_transfer_history && details.vehicle_transfer_history.length > 0 && details.vehicle_transfer_history.map((item, index) => (
												<tr key={index}>
													<td>{index + 1}</td>
													<td>{item.transferDate}</td>
													<td>{item.fromProjectName}</td>
													<td>{item.toProjectName}</td>
													<td>{item.fromUserName} ({item.fromDesignation})</td>
													<td>{item.toUserName} ({item.toDesignation})</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
							<div className='col-md-6'>
								<div className="mt-5" style={{ height: "650px" }}>
									<Calendar
										events={transferHistory}
										startAccessor="start"
										endAccessor="end"
										defaultDate={moment().toDate()}
										localizer={localizer}
										views={{ month: true }}
										components={{
											event: Event
										}}
										eventPropGetter={event => ({
											style: {
												backgroundColor: '#009ef7'
											},
										})}
									/>
								</div>
							</div>
						</div>
					}

					{tab === "vehicleExpenseList" &&
						<VehicleExpenseList idVmsVehicleInfo={id} />
					}
					{tab === "vehicleFiles" &&
						<>
							<div className='row'>
								<div className='col-md-6'>
									<form onSubmit={handleAttachedImageSubmit}>
										<div className="mt-5">
											<div className="form-group row">
												<label htmlFor="imageAttachment" className="col-lg-4 col-form-label" style={{ "textAlign": "right" }}>Attach Image </label>
												<div className="col-lg-8">
													<Form.Control type="file" size="sm" name="imageAttachment" id="imageAttachment" onChange={onImageChange} onClick={e => (e.target.value = null)} />
												</div>
											</div>
											<div className="form-group row">
												<label htmlFor="imageTitle" className="col-lg-4 col-form-label" style={{ "textAlign": "right" }} >Image Title</label>
												<div className="col-lg-8">
													<input type="text" name="imageTitle" id="imageTitle" value={imageTitle} className="form-control form-control-sm" onChange={handleInputOnChange} />
												</div>
											</div>
										</div>
										<div className="row">
											<div className="col-6">
											</div>
											<div className="col-6">
												<button type="submit" disabled={isLoading === true} className="btn btn-success btn-sm mb-5">Attach</button>
												<button type="reset" className="btn btn-danger btn-sm mb-5" onClick={() => resetAttachedUploadImage()}>Reset</button>
												{isLoading === true &&
													<ReactLoading type={"bubbles"} color={"#4739ea3d"} height={'15%'} width={'15%'} />
												}
											</div>
										</div>
									</form>
								</div>
							</div>
							<div className='row'>
								<div className="col-6">
									<div className='table-responsive' style={{ "overflowY": "auto" }}>
										<table className="table table-bordered table-hover mt-2">
											<thead>
												<tr>
													<td>Sl</td>
													<td>Image</td>
													<td>Name</td>
													<td>Title</td>
													<td style={{ "textAlign": "center" }}>Main Image</td>
													<td style={{ "textAlign": "center" }}>Action</td>
												</tr>
											</thead>
											{image.length > 0 ?
												<tbody>
													{image.map((item, index) =>
														<tr key={index}>
															<td>{index + 1}</td>
															<td>
																<img src={`${filehost}${imagePath}/thumb/${item.thumbnailName}`} alt="" style={{ width: "100px", height: "100px", objectFit: "cover" }} />
															</td>
															<td>{item.originalFileName}</td>
															<td>{item.title}</td>
															<td style={{ "padding": "0.75rem", "textAlign": "center" }} onClick={() => isMainChange(item.idVmsVehicleImages)}>
																{item.isMain === '1' &&
																	<KTSVG
																		path='/media/icons/duotune/arrows/arr012.svg'
																		className='svg-icon-lg svg-icon-success'
																	/>
																}
																{item.isMain === '0' &&
																	<KTSVG
																		path='/media/icons/duotune/arrows/arr011.svg'
																		className='svg-icon-lg svg-icon-danger'
																	/>
																}
															</td>
															<td style={{ "textAlign": "center" }}>
																<a href={`${filehost}${imagePath}/${item.fileName}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary">
																	<span >
																		<KTSVG
																			path='/media/svg/icons/Files/DownloadedFile.svg'
																			className='svg-icon svg-icon-1'
																		/>
																	</span>
																</a>
																<button className="btn btn-sm btn-outline btn-outline-dashed btn-outline-danger btn-active-light-danger" onClick={() => { removeFile(item.idVmsVehicleImages) }}>
																	<span >
																		<KTSVG
																			path='/media/svg/icons/General/Trash.svg'
																			className='svg-icon svg-icon-1'
																		/>
																	</span>
																</button>
															</td>
														</tr>
													)}
												</tbody>
												:
												<tbody>
													<tr>
														<td colSpan="6"><p style={{ "textAlign": "center" }}><strong>No Image Found</strong></p></td>
													</tr>
												</tbody>
											}
										</table>
									</div>
								</div>
								<div className="col-6">
									<div className='table-responsive' style={{ "overflowY": "auto" }}>
										<table className="table table-bordered table-hover mt-2">
											<thead>
												<tr>
													<td>Sl</td>
													<td>Name</td>
													<td>Size</td>
													<td style={{ "textAlign": "center" }}>Action</td>
												</tr>
											</thead>
											{file.length > 0 ?
												<tbody>
													{file.map((item, index) =>
														<tr key={index}>
															<td>{index + 1}</td>
															<td>{item.originalFileName}</td>
															<td>{item.fileSize}</td>
															<td style={{ "textAlign": "center" }}>
																<a href={`${filehost}${filePath}/${item.fileName}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary mr-3"><i className="flaticon2-download-1"></i> Download</a>
															</td>
														</tr>
													)}
												</tbody>
												:
												<tbody>
													<tr>
														<td colSpan="4"><p style={{ "textAlign": "center" }}><strong>No File Found</strong></p></td>
													</tr>
												</tbody>
											}
										</table>
									</div>
								</div>
							</div>
						</>
					}
				</div>
				{/* <pre>{JSON.stringify(transferHistory,null,2)}</pre> */}
			</div>
		</>
	)
}

export default DetailsVehicle;